/**
 * MFA slice — owns the intermediate "mfaRequired" step of login
 * (design: store/auth/mfaSlice.ts for the mfaRequired step).
 *
 * Wire contract (auth-service STAGE A — the single source of truth):
 * - Login challenge:  { mfaRequired, status: 'NOT_ENROLLED'|'ENROLLED',
 *                       preAuthJwt, preAuthTtlSeconds }
 * - PUT  /auth/mfa/enroll   body { preAuthJwt }
 *   → { otpauthUri, secret, recoveryCodes, nextPreAuthJwt }
 * - POST /auth/mfa/confirm  body { preAuthJwt, code }
 *   → first sequential code  → { confirmed:false, nextPreAuthJwt }  (NO session)
 *   → second sequential code → { confirmed:true, accessToken, refreshToken, policy }
 * - POST /auth/login/mfa    body { preAuthJwt, code | recoveryCode }
 *   → { accessToken, refreshToken, policy }
 *
 * The challenge is SINGLE-USE: every consumed ticket is replaced by the
 * server-returned `nextPreAuthJwt`. Tokens are adopted ONLY on a final
 * confirmed:true / verified response.
 *
 * SECURITY: `preAuthJwt`, `otpauthUri` (embedded TOTP secret), `secret` and
 * `recoveryCodes` live in this Redux slice — process memory only. They are
 * NEVER written to localStorage/sessionStorage. Redux is intentionally not
 * persisted; do not add persistence middleware for this slice.
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import {
  adoptSession,
  confirmMfaEnrollment,
  enrollMfa,
  fetchCurrentUser,
  verifyMfaLogin,
} from '@/lib/auth';
import { loginUser, logoutUser, resetAuth, type LoginResult } from './authSlice';
import type { MfaConfirmResponse, User } from '@/types';

export type MfaStep = 'idle' | 'verify' | 'setup' | 'recovery';

interface MfaState {
  step: MfaStep;
  /** Short-lived single-use challenge JWT from the mfaRequired login response. */
  preAuthJwt: string | null;
  /** otpauth:// URI from enrollment — contains the shared secret, memory only. */
  otpauthUri: string | null;
  /** Returned ONCE by /auth/mfa/enroll; shown once after enrollment completes. */
  recoveryCodes: string[];
  /** True after the first sequential confirm code was accepted (second pending). */
  firstCodeAccepted: boolean;
  /** Set when the final step succeeded (session adopted) but a screen still gates it. */
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: MfaState = {
  step: 'idle',
  preAuthJwt: null,
  otpauthUri: null,
  recoveryCodes: [],
  firstCodeAccepted: false,
  user: null,
  isLoading: false,
  error: null,
};

function messageFromError(err: unknown): string {
  const anyErr = err as { response?: { data?: { message?: string } } };
  if (anyErr?.response?.data?.message) return anyErr.response.data.message;
  return err instanceof Error ? err.message : 'MFA verification failed.';
}

/** Result of the enrollment-confirm thunk (discriminated wire branches). */
export type MfaConfirmOutcome =
  /** First sequential code accepted: no session; next code + refreshed ticket. */
  | { kind: 'pending'; nextPreAuthJwt?: string }
  /** Enrollment complete: session adopted via AuthService (sole issuer). */
  | { kind: 'complete'; user: User; token: string };

/** PUT /auth/mfa/enroll — fetch the otpauth URI + recovery codes + next ticket. */
export const performMfaEnrollment = createAsyncThunk<
  { otpauthUri: string; recoveryCodes: string[]; nextPreAuthJwt: string },
  void,
  { state: { mfa: MfaState }; rejectValue: string }
>('mfa/enroll', async (_, { getState, rejectWithValue }) => {
  const { preAuthJwt } = getState().mfa;
  if (!preAuthJwt)
    return rejectWithValue('Your verification session expired. Please sign in again.');
  try {
    const data = await enrollMfa(preAuthJwt);
    return {
      otpauthUri: data.otpauthUri,
      recoveryCodes: data.recoveryCodes,
      nextPreAuthJwt: data.nextPreAuthJwt,
    };
  } catch (err) {
    return rejectWithValue(messageFromError(err));
  }
});

export interface MfaCompletion {
  user: User;
  /** Access token of the adopted session (for Redux auth state). */
  token: string;
}

/**
 * POST /auth/mfa/confirm — one submission of the two-sequential-code flow.
 * The consumed single-use ticket is replaced by `nextPreAuthJwt`; on the
 * final confirmed:true branch the returned session is adopted (tokens are
 * persisted ONLY then) and the current user is fetched.
 */
export const confirmMfaEnrollmentStep = createAsyncThunk<
  MfaConfirmOutcome,
  string,
  { state: { mfa: MfaState }; rejectValue: string }
>('mfa/confirm', async (code, { getState, rejectWithValue }) => {
  const { preAuthJwt } = getState().mfa;
  if (!preAuthJwt)
    return rejectWithValue('Your verification session expired. Please sign in again.');
  try {
    const data: MfaConfirmResponse = await confirmMfaEnrollment(preAuthJwt, code);
    if (data.confirmed === false) {
      // First sequential code: NO session exists yet — never fabricate one.
      return { kind: 'pending', nextPreAuthJwt: data.nextPreAuthJwt };
    }
    // Second sequential code: auth-service minted the MFA-verified session.
    adoptSession({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    const user = await fetchCurrentUser();
    return { kind: 'complete', user, token: data.accessToken };
  } catch (err) {
    return rejectWithValue(messageFromError(err));
  }
});

/** POST /auth/login/mfa — verify the code for an already-enrolled user. */
export const verifyMfaCode = createAsyncThunk<
  MfaCompletion,
  string,
  { state: { mfa: MfaState }; rejectValue: string }
>('mfa/verifyLogin', async (code, { getState, rejectWithValue }) => {
  const { preAuthJwt } = getState().mfa;
  if (!preAuthJwt)
    return rejectWithValue('Your verification session expired. Please sign in again.');
  try {
    const tokens = await verifyMfaLogin(preAuthJwt, code);
    adoptSession(tokens);
    const user = await fetchCurrentUser();
    return { user, token: tokens.accessToken };
  } catch (err) {
    return rejectWithValue(messageFromError(err));
  }
});

const mfaSlice = createSlice({
  name: 'mfa',
  initialState,
  reducers: {
    mfaReset: (state) => {
      // Wipe every sensitive field from memory.
      state.step = 'idle';
      state.preAuthJwt = null;
      state.otpauthUri = null;
      state.recoveryCodes = [];
      state.firstCodeAccepted = false;
      state.user = null;
      state.isLoading = false;
      state.error = null;
    },
    clearMfaError(state) {
      state.error = null;
    },
    /** User acknowledged the one-time recovery codes screen. */
    mfaRecoveryAcknowledged(state) {
      state.step = 'idle';
      state.preAuthJwt = null;
      state.otpauthUri = null;
      state.recoveryCodes = [];
      state.firstCodeAccepted = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login handed us the MFA step (enrollment derived from wire `status`).
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<LoginResult>) => {
        if (action.payload.kind !== 'mfa') return;
        state.preAuthJwt = action.payload.preAuthJwt;
        state.step = action.payload.status === 'NOT_ENROLLED' ? 'setup' : 'verify';
        state.error = null;
        state.recoveryCodes = [];
        state.otpauthUri = null;
        state.firstCodeAccepted = false;
      })
      .addCase(performMfaEnrollment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(performMfaEnrollment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.otpauthUri = action.payload.otpauthUri;
        // Recovery codes come from ENROLL — kept for the single one-time
        // display after enrollment completes.
        state.recoveryCodes = action.payload.recoveryCodes;
        // The enroll call burned the login challenge; adopt the fresh ticket.
        state.preAuthJwt = action.payload.nextPreAuthJwt;
      })
      .addCase(performMfaEnrollment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to start MFA setup.';
      })
      .addCase(confirmMfaEnrollmentStep.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        confirmMfaEnrollmentStep.fulfilled,
        (state, action: PayloadAction<MfaConfirmOutcome>) => {
          state.isLoading = false;
          if (action.payload.kind === 'pending') {
            // First code accepted: hold no session; refresh the single-use
            // ticket and ask for the NEXT authenticator code.
            state.firstCodeAccepted = true;
            state.preAuthJwt = action.payload.nextPreAuthJwt ?? null;
            if (!state.preAuthJwt) {
              state.error =
                'Your verification session expired. Please sign in again.';
            }
            return;
          }
          // Enrollment complete: session adopted; keep recovery codes for the
          // one-time display, then gate navigation.
          state.user = action.payload.user;
          state.step = state.recoveryCodes.length ? 'recovery' : 'idle';
          if (state.step === 'idle') {
            state.preAuthJwt = null;
            state.otpauthUri = null;
            state.recoveryCodes = [];
            state.firstCodeAccepted = false;
          }
        },
      )
      .addCase(confirmMfaEnrollmentStep.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'MFA enrollment failed.';
      })
      .addCase(verifyMfaCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyMfaCode.fulfilled, (state, action: PayloadAction<MfaCompletion>) => {
        // Success: session adopted; LoginPage navigates. Clear all MFA memory.
        state.step = 'idle';
        state.preAuthJwt = null;
        state.otpauthUri = null;
        state.recoveryCodes = [];
        state.firstCodeAccepted = false;
        state.user = action.payload.user;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(verifyMfaCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Invalid code. Please try again.';
      })
      // Leaving the account / logging out wipes the pending MFA state.
      .addCase(resetAuth, () => initialState)
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export const { mfaReset, clearMfaError, mfaRecoveryAcknowledged } = mfaSlice.actions;
export default mfaSlice.reducer;
